import {
  getNavigationPanelRootSectionFallback,
  type NavigationPanelRootSection,
  NavigationPanelService,
} from '@affine/core/modules/navigation-panel';
import { useLiveData, useService } from '@toeverything/infra';
import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';

type NavigationAccordionContextValue = {
  activeSection: NavigationPanelRootSection;
  registerSection: (section: NavigationPanelRootSection) => () => void;
};

const NavigationAccordionContext =
  createContext<NavigationAccordionContextValue | null>(null);

export const NavigationPanelAccordion = ({ children }: PropsWithChildren) => {
  const navigationPanelService = useService(NavigationPanelService);
  const activeSection = useLiveData(
    navigationPanelService.activeRootSection$()
  );
  const [sections, setSections] = useState<NavigationPanelRootSection[]>([]);

  const registerSection = useCallback((section: NavigationPanelRootSection) => {
    setSections(sections =>
      sections.includes(section) ? sections : [...sections, section]
    );

    return () => {
      setSections(sections => sections.filter(item => item !== section));
    };
  }, []);

  useLayoutEffect(() => {
    if (sections.includes(activeSection)) {
      return;
    }

    const fallback = getNavigationPanelRootSectionFallback(sections);
    if (fallback) {
      navigationPanelService.activateRootSection(fallback);
    }
  }, [activeSection, navigationPanelService, sections]);

  const contextValue = useMemo(
    () => ({ activeSection, registerSection }),
    [activeSection, registerSection]
  );

  return (
    <NavigationAccordionContext.Provider value={contextValue}>
      {children}
    </NavigationAccordionContext.Provider>
  );
};

export const useNavigationPanelAccordionState = (
  section: NavigationPanelRootSection | undefined
) => {
  const accordion = useContext(NavigationAccordionContext);

  if (!accordion || !section) {
    return null;
  }

  return {
    collapsed: accordion.activeSection !== section,
  };
};

export const useRegisterNavigationPanelAccordionSection = (
  section: NavigationPanelRootSection | undefined
) => {
  const accordion = useContext(NavigationAccordionContext);
  const registerSection = accordion?.registerSection;

  useLayoutEffect(() => {
    if (!registerSection || !section) {
      return;
    }

    return registerSection(section);
  }, [registerSection, section]);

  return useNavigationPanelAccordionState(section);
};
